/**
 * 현재 화면을 Screen Capture API로 캡처합니다.
 * 피드백 모달이 열리기 전에 호출해야 모달이 스크린샷에 포함되지 않습니다.
 */
export async function captureScreenshot(): Promise<Blob | null> {
  try {
    // 화면 공유 요청 (현재 탭 우선)
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        displaySurface: 'browser',
      },
      audio: false,
      // preferCurrentTab: Chrome 94+에서 현재 탭 자동 선택 (비표준)
      preferCurrentTab: true,
    } as DisplayMediaStreamOptions & { preferCurrentTab?: boolean });

    const track = stream.getVideoTracks()[0];

    // Canvas로 변환하는 공통 헬퍼
    const blobFromCanvas = (canvas: HTMLCanvasElement): Promise<Blob | null> =>
      new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9);
      });

    // ImageCapture API 지원 여부 확인
    if (typeof ImageCapture !== 'undefined') {
      const imageCapture = new ImageCapture(track);
      const bitmap = await imageCapture.grabFrame();

      track.stop();

      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(bitmap, 0, 0);
      bitmap.close();

      return blobFromCanvas(canvas);
    }

    // ImageCapture 미지원 브라우저 fallback (Firefox 등): video element 사용
    const video = document.createElement('video');
    video.srcObject = stream;
    await video.play();

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')!.drawImage(video, 0, 0);

    video.pause();
    video.srcObject = null;
    track.stop();

    return blobFromCanvas(canvas);
  } catch (err) {
    if (err instanceof Error && err.name === 'NotAllowedError') {
      // 사용자가 화면 공유를 취소한 경우 — 경고 없이 null 반환
      return null;
    }
    console.warn('[diagnostics] 스크린샷 캡처 실패:', err);
    return null;
  }
}
